import React, { useState } from 'react';
import { useI18n } from '../../utils/i18n';
import Schalter from './Schalter';

// #574: Einstellungen der Voicemail-Pipeline im KI-Reiter, Rahmen
// „Anrufbeantworter“ (Beschlüsse 04.11.2026). Geprüft wird auf dem Server
// (server/utils/voicemailEinstellungen.js), hier steht nur das Formular.
// Gespeichert wird mit „Speichern“ des Reiters (voicemailNutzlast).
//
// Die Absender stehen im Formular als Text (eine Adresse je Zeile), sonst
// ließe sich keine neue Zeile tippen; erst die Nutzlast macht eine Liste daraus.

const SPRACHEN = [['de', 'Deutsch'], ['en', 'English'], ['es', 'Español']];
const BETREFF_MAX = 1000;

export const absenderListe = (wert) => (Array.isArray(wert) ? wert : String(wert || '').split(/[\n,;]/))
  .map(s => String(s).trim()).filter(Boolean);

export function voicemailNutzlast(einst) {
  if (!einst || typeof einst !== 'object') return undefined;
  return { ...einst, absender: absenderListe(einst.absender) };
}

// Testfeld: dieselbe Auswertung wie auf dem Server (Flag i, Gruppen name/nummer,
// Betreff bis 1000 Zeichen, Name ohne Wiederholung)
export function musterTest(muster, betreff) {
  let re;
  try { re = new RegExp(muster, 'i'); } catch (_) { return { ungueltig: true }; }
  const m = re.exec(String(betreff || '').slice(0, BETREFF_MAX));
  if (!m) return { passt: false };
  const g = m.groups || {};
  const name = [...new Set(String(g.name || '').trim().split(',').map(s => s.trim()).filter(Boolean))].join(', ');
  return { passt: true, name: name || null, nummer: String(g.nummer || '').trim() || null };
}

const VoicemailEinstellungen = ({ einst, vorgaben, users, onChange }) => {
  const { t } = useI18n();
  const [testBetreff, setTestBetreff] = useState('');
  if (!einst) return null;
  const setze = (feld, wert) => onChange({ ...einst, [feld]: wert });
  const absenderText = Array.isArray(einst.absender) ? einst.absender.join('\n') : String(einst.absender || '');
  const test = testBetreff ? musterTest(einst.betreffMuster || '', testBetreff) : null;
  const zuweisbar = (Array.isArray(users) ? users : [])
    .filter(u => u && u.email && u.isActive !== false && (u.isTicketworker || u.isManager))
    .sort((a, b) => String(a.displayName || a.email).localeCompare(String(b.displayName || b.email)));
  const zuweisungUnbekannt = einst.zuweisung && !zuweisbar.some(u => u.email.toLowerCase() === einst.zuweisung);

  return (
    <>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1, minWidth: 260 }}>
          <label>{t('admin.vm.absender', 'Absender der AB-Mails')}</label>
          <textarea rows="2" value={absenderText} onChange={(e) => setze('absender', e.target.value)} />
          <div className="settings-help" style={{ marginTop: 6 }}>
            <small>{t('admin.vm.absenderHint', 'Eine Adresse je Zeile. Nur Mails dieser Absender werden als AB-Nachricht verarbeitet.')}</small>
          </div>
        </div>
        <div className="form-group" style={{ flex: 2, minWidth: 300 }}>
          <label>{t('admin.vm.muster', 'Betreffmuster (regulärer Ausdruck)')}</label>
          <input type="text" value={einst.betreffMuster || ''} onChange={(e) => setze('betreffMuster', e.target.value)} spellCheck={false} />
          <div className="settings-help" style={{ marginTop: 6 }}>
            <small>{t('admin.vm.musterHint', 'Groß-/Kleinschreibung egal. Die Gruppen (?<name>…) und (?<nummer>…) liefern Anrufer und Nummer.')}</small>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>{t('admin.vm.test', 'Muster testen')}</label>
          <input type="text" value={testBetreff} onChange={(e) => setTestBetreff(e.target.value)} placeholder={t('admin.vm.testPlatzhalter', 'Betreff einer AB-Mail einfügen')} />
          {test && (
            <div className="settings-help" style={{ marginTop: 6 }} role="status">
              <small>
                {test.ungueltig && t('admin.vm.testUngueltig', 'Das Muster ist kein gültiger regulärer Ausdruck.')}
                {test.passt === false && t('admin.vm.testPasstNicht', 'Passt nicht. Diese Mail würde nicht als AB-Nachricht verarbeitet.')}
                {test.passt && t('admin.vm.testPasst', 'Passt. Anrufer: {{name}}, Nummer: {{nummer}}', { name: test.name || '–', nummer: test.nummer || '–' })}
              </small>
            </div>
          )}
        </div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ width: 200 }}>
          <label>{t('admin.vm.sprache', 'Sprache')}</label>
          <select value={einst.sprache || 'de'} onChange={(e) => setze('sprache', e.target.value)}>
            {SPRACHEN.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
          <div className="settings-help" style={{ marginTop: 6 }}>
            <small>{t('admin.vm.spracheHint', 'Für Transkription und Beschreibung.')}</small>
          </div>
        </div>
        <div className="form-group" style={{ width: 260 }}>
          <label>{t('admin.vm.beschreibung', 'KI-Beschreibung')}</label>
          <Schalter t={t} an={einst.beschreibungAn !== false} onChange={(an) => setze('beschreibungAn', an)} />
          <div className="settings-help" style={{ marginTop: 6 }}>
            <small>{t('admin.vm.beschreibungHint', 'Aus: keine zweite KI-Anfrage, das Transkript steht in der Beschreibung, Priorität normal.')}</small>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>{t('admin.vm.prompt', 'Prompt der Beschreibung')}</label>
          <textarea rows="4" value={einst.prompt || ''} disabled={einst.beschreibungAn === false} onChange={(e) => setze('prompt', e.target.value)} />
          <div className="settings-help" style={{ marginTop: 6 }}>
            <small>{t('admin.vm.promptHint', '{sprache} wird durch die Sprache ersetzt. Die Antwort muss ein JSON-Objekt mit summary und priority sein.')}</small>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
          <label>{t('admin.vm.titel', 'Titelvorlage')}</label>
          <input type="text" value={einst.titelVorlage || ''} onChange={(e) => setze('titelVorlage', e.target.value)} />
          <div className="settings-help" style={{ marginTop: 6 }}>
            <small>{t('admin.vm.titelHint', 'Platzhalter: {anrufer} (Name, sonst Nummer), {name}, {nummer}.')}</small>
          </div>
        </div>
        <div className="form-group" style={{ width: 220 }}>
          <label>{t('admin.vm.kategorie', 'Kategorie')}</label>
          <input type="text" value={einst.kategorie || ''} onChange={(e) => setze('kategorie', e.target.value)} />
        </div>
        <div className="form-group" style={{ width: 260 }}>
          <label>{t('admin.vm.zuweisung', 'Zuweisung')}</label>
          <select value={einst.zuweisung || ''} onChange={(e) => setze('zuweisung', e.target.value || null)}>
            <option value="">{t('admin.vm.pool', 'Pool (niemand)')}</option>
            {zuweisungUnbekannt && <option value={einst.zuweisung}>{einst.zuweisung}</option>}
            {zuweisbar.map(u => <option key={u.email} value={u.email.toLowerCase()}>{u.displayName ? `${u.displayName} (${u.email})` : u.email}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ width: 260 }}>
          <label>{t('admin.vm.intern', 'Transkript als interner Kommentar')}</label>
          <Schalter t={t} an={einst.kommentarIntern === true} onChange={(an) => setze('kommentarIntern', an)} />
        </div>
      </div>
      {vorgaben && (
        <div className="settings-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onChange({ ...vorgaben, absender: [...(vorgaben.absender || [])] })}>
            {t('admin.vm.zuruecksetzen', 'Anrufbeantworter auf Vorgaben zurücksetzen')}
          </button>
        </div>
      )}
    </>
  );
};

export default VoicemailEinstellungen;
